/**
 * How a tap is graded.
 *
 * The rule this phase adopts is deliberately narrow: **a wave always dies when
 * its centre is tapped.** There is no window in which the tap is refused, no
 * "too early" and no "too late" that makes the input do nothing. What varies is
 * only the grade, the score and how the wave looks as it goes.
 *
 * That is why `miss` is not a timing bucket like the others. A tap can never
 * produce it — the worst a tap can do is `bad`. `miss` means the player never
 * touched the wave at all and it expired on its own, which is a different fact
 * about the play and needs to stay distinguishable in the counts.
 *
 * Every threshold lives here. Nothing downstream re-derives a window from a
 * literal, so retuning the feel is one edit to `DEFAULT_JUDGE_SETTINGS` — and
 * the renderer colours its timing arc from these same numbers, so what the
 * player sees cannot drift away from what they get.
 */

export const TIMING_GRADES = ['perfect', 'great', 'good', 'bad', 'miss'] as const;
export type TimingGrade = (typeof TIMING_GRADES)[number];

/** Which side of the best moment the tap landed on. */
export type TimingDirection = 'early' | 'late' | 'exact';

export interface JudgeSettings {
  /** `|dt|` at or below this is PERFECT, in seconds. */
  readonly perfectSeconds: number;
  /** Upper bound of GREAT. */
  readonly greatSeconds: number;
  /** Upper bound of GOOD. Beyond it is BAD, and BAD has no upper bound. */
  readonly goodSeconds: number;
  readonly scores: Readonly<Record<TimingGrade, number>>;
  /** Grades that send the combo back to zero. */
  readonly comboBreakingGrades: readonly TimingGrade[];
  /**
   * Grades good enough to actually clear the wave.
   *
   * The rule this phase turns on. A tap outside these leaves the wave standing,
   * so the player can settle and try the same wave again — badly timed input
   * costs the combo instead of buying a way past it.
   */
  readonly clearingGrades: readonly TimingGrade[];
  /**
   * How long the same wave refuses further input after a failed tap.
   *
   * Without it the wave surviving a BAD would simply make mashing the optimal
   * strategy: every frame would be another free attempt. This is short enough
   * to be invisible when playing honestly and long enough that spamming buys
   * far fewer attempts than the frame rate would allow.
   */
  readonly badCooldownSeconds: number;
  /** Dissolve length per grade, seconds. Only clearing grades and `miss` use it. */
  readonly fadeSeconds: Readonly<Record<TimingGrade, number>>;
  /**
   * Radius of the tappable disc at a wave's origin, in world units.
   *
   * Independent of every visual ring on purpose: the seal's inscription grows
   * and rotates, and none of that may change where the player has to press.
   */
  readonly noteHitRadius: number;
  /** How long a resolved wave keeps drawing while it dissolves, in seconds. */
  readonly resolveFadeSeconds: number;
  /** `|dt|` below this reads as neither early nor late. */
  readonly exactEpsilonSeconds: number;
}

export const DEFAULT_JUDGE_SETTINGS: JudgeSettings = Object.freeze({
  perfectSeconds: 0.06,
  greatSeconds: 0.12,
  goodSeconds: 0.2,
  scores: Object.freeze({ perfect: 1000, great: 700, good: 400, bad: 0, miss: 0 }),
  // A failed tap now costs the combo, because it no longer costs the wave.
  comboBreakingGrades: Object.freeze(['bad', 'miss'] as const),
  clearingGrades: Object.freeze(['perfect', 'great', 'good'] as const),
  badCooldownSeconds: 0.15,
  noteHitRadius: 1.25,
  // Longer for the grades worth watching. A PERFECT should be the one that
  // hangs in the air; a miss should be gone before it is noticed.
  fadeSeconds: Object.freeze({
    perfect: 0.52,
    great: 0.42,
    good: 0.32,
    bad: 0,
    miss: 0.28,
  }),
  resolveFadeSeconds: 0.52,
  exactEpsilonSeconds: 0.004,
});

/** Whether this grade removes the wave. */
export function clearsWave(
  grade: TimingGrade,
  settings: JudgeSettings = DEFAULT_JUDGE_SETTINGS,
): boolean {
  return settings.clearingGrades.includes(grade);
}

/** Dissolve length for a grade, seconds. */
export function fadeForGrade(
  grade: TimingGrade,
  settings: JudgeSettings = DEFAULT_JUDGE_SETTINGS,
): number {
  return settings.fadeSeconds[grade] ?? settings.resolveFadeSeconds;
}

/** The result of one wave leaving play, tapped or not. */
export interface WaveJudgement {
  readonly grade: TimingGrade;
  /** `tapSongTime - bestTime`, in milliseconds. Negative is early. */
  readonly timingOffsetMs: number;
  readonly timingDirection: TimingDirection;
  readonly scoreAwarded: number;
  /** Combo *after* this judgement was applied. */
  readonly combo: number;
  /** False when the wave expired untouched. */
  readonly tapped: boolean;
}

/**
 * A failed tap: the wave was hit but not cleared.
 *
 * Kept separate from `WaveJudgement` because it is not a resolution. The wave
 * is still standing, still tappable, and will get exactly one judgement in the
 * end however many of these it collects on the way.
 */
export interface WaveAttempt {
  readonly grade: Extract<TimingGrade, 'bad'>;
  readonly timingOffsetMs: number;
  readonly timingDirection: TimingDirection;
  /** How many failed taps this wave has now taken. */
  readonly attempt: number;
}

/**
 * Grades a tap offset.
 *
 * Never returns `miss`: a tap that lands is always at least `bad`, because the
 * wave is always removed. Boundaries are inclusive at the tighter grade, so an
 * offset of exactly 60 ms is PERFECT rather than GREAT.
 */
export function gradeForOffset(
  offsetSeconds: number,
  settings: JudgeSettings = DEFAULT_JUDGE_SETTINGS,
): Exclude<TimingGrade, 'miss'> {
  const magnitude = Math.abs(offsetSeconds);
  if (!Number.isFinite(magnitude)) return 'bad';
  if (magnitude <= settings.perfectSeconds) return 'perfect';
  if (magnitude <= settings.greatSeconds) return 'great';
  if (magnitude <= settings.goodSeconds) return 'good';
  return 'bad';
}

export function directionForOffset(
  offsetSeconds: number,
  settings: JudgeSettings = DEFAULT_JUDGE_SETTINGS,
): TimingDirection {
  if (Math.abs(offsetSeconds) < settings.exactEpsilonSeconds) return 'exact';
  return offsetSeconds < 0 ? 'early' : 'late';
}

export function scoreForGrade(
  grade: TimingGrade,
  settings: JudgeSettings = DEFAULT_JUDGE_SETTINGS,
): number {
  return settings.scores[grade] ?? 0;
}

export function breaksCombo(
  grade: TimingGrade,
  settings: JudgeSettings = DEFAULT_JUDGE_SETTINGS,
): boolean {
  return settings.comboBreakingGrades.includes(grade);
}

/** Stable small integer per grade, for packing into a shader attribute. */
export function gradeCode(grade: TimingGrade): number {
  return TIMING_GRADES.indexOf(grade);
}
